import React, { useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { reviewService } from '../services/reviewService';
import { lessonService } from '../services/lessonService';
import type { Lesson } from '../types';

const getPriorityColor = (hours: number): 'error' | 'warning' | 'default' => {
  if (hours > 48) return 'error';
  if (hours > 24) return 'warning';
  return 'default';
};

const getHoursAgo = (dateStr: string): number => {
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60));
};

const LessonReviewCard: React.FC<{ lesson: Lesson; onClick: () => void }> = ({
  lesson,
  onClick,
}) => {
  const submittedDate = lesson.updated_at;
  const hoursAgo = getHoursAgo(submittedDate);
  const priorityColor = getPriorityColor(hoursAgo);

  const accentColor =
    priorityColor === 'error'
      ? 'error.main'
      : priorityColor === 'warning'
      ? 'warning.main'
      : 'divider';

  return (
    <Card
      elevation={0}
      sx={{
        mb: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        display: 'flex',
        overflow: 'hidden',
        transition: 'border-color 0.15s ease',
        '&:hover': {
          borderColor: 'primary.main',
        },
      }}
    >
      {/* Priority accent bar */}
      <Box
        sx={{
          width: 4,
          flexShrink: 0,
          borderRadius: '4px 0 0 4px',
          bgcolor: accentColor,
        }}
      />

      <CardActionArea onClick={onClick} sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
          {/* Left: content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="h6" fontWeight={700} noWrap>
                {lesson.title}
              </Typography>
              {priorityColor !== 'default' && (
                <Chip
                  icon={<PriorityHighIcon />}
                  label={priorityColor === 'error' ? 'Urgent' : 'High'}
                  size="small"
                  color={priorityColor}
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {lesson.topic} &bull; {lesson.cefr_level} &bull; {lesson.duration_min} min
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Author: {lesson.author?.name ?? lesson.author_id}
            </Typography>
          </Box>

          {/* Right: status + time */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, flexShrink: 0 }}>
            <Chip
              label={lesson.status.replace('_', ' ')}
              size="small"
              color={lesson.status === 'approved' ? 'success' : 'warning'}
            />
            <Chip
              icon={<AccessTimeIcon />}
              label={`${hoursAgo}h ago`}
              size="small"
              variant="outlined"
              sx={{ typography: 'caption' }}
            />
          </Box>
        </Box>
      </CardActionArea>
    </Card>
  );
};

export const ContentManagerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const {
    data: pendingLessons = [],
    isLoading: isPendingLoading,
    isError: isPendingError,
  } = useQuery({
    queryKey: ['reviews', 'pending'],
    queryFn: reviewService.getPendingReviews,
    refetchInterval: 30_000,
  });

  const {
    data: approvedData,
    isLoading: isApprovedLoading,
    isError: isApprovedError,
  } = useQuery({
    queryKey: ['lessons', 'approved'],
    queryFn: () => lessonService.getAll({ status: 'approved' }),
    enabled: activeTab === 1,
  });

  const approvedLessons: Lesson[] = approvedData?.data ?? [];

  const renderLessonList = (lessons: Lesson[], isLoading: boolean, isError: boolean) => {
    if (isLoading) {
      return (
        <Box>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={110} sx={{ mb: 1.5, borderRadius: 2 }} />
          ))}
        </Box>
      );
    }
    if (isError) {
      return <Alert severity="error">Failed to load lessons.</Alert>;
    }
    if (lessons.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 8, color: 'text.disabled' }}>
          {activeTab === 1 ? (
            <CheckCircleIcon sx={{ fontSize: 48, mb: 1 }} />
          ) : (
            <AccessTimeIcon sx={{ fontSize: 48, mb: 1 }} />
          )}
          <Typography variant="h6">No lessons here</Typography>
          <Typography variant="body2">Check back later.</Typography>
        </Box>
      );
    }
    return lessons.map((lesson) => (
      <LessonReviewCard
        key={lesson.id}
        lesson={lesson}
        onClick={() => navigate(`/lessons/${lesson.id}/review`)}
      />
    ));
  };

  const urgentCount = pendingLessons.filter((l) => getHoursAgo(l.updated_at) > 48).length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      {/* Page header */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          bgcolor: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: 'white' }}>
            Review Queue
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.25 }}>
            {pendingLessons.length} lesson{pendingLessons.length !== 1 ? 's' : ''} pending review
            {urgentCount > 0 && ` \u00b7 ${urgentCount} urgent`}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {isPendingLoading && <CircularProgress size={22} sx={{ color: 'white' }} />}
          {urgentCount > 0 && (
            <Chip
              icon={<PriorityHighIcon />}
              label={`${urgentCount} Urgent`}
              size="small"
              sx={{
                bgcolor: 'error.main',
                color: 'white',
                '& .MuiChip-icon': { color: 'white' },
              }}
            />
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Box
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 3,
          bgcolor: 'background.paper',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          TabIndicatorProps={{ style: { backgroundColor: 'var(--mui-palette-primary-main)' } }}
        >
          <Tab
            label={
              <Badge
                badgeContent={pendingLessons.length}
                color="warning"
                sx={{ '& .MuiBadge-badge': { right: -10, top: -2 } }}
              >
                <Box sx={{ pr: pendingLessons.length > 0 ? 1.5 : 0 }}>Pending Review</Box>
              </Badge>
            }
          />
          <Tab
            label={
              <Badge
                badgeContent={approvedLessons.length}
                color="success"
                sx={{ '& .MuiBadge-badge': { right: -10, top: -2 } }}
              >
                <Box sx={{ pr: approvedLessons.length > 0 ? 1.5 : 0 }}>Ready to Publish</Box>
              </Badge>
            }
          />
        </Tabs>
      </Box>

      {/* Content */}
      <Box sx={{ px: 3, pt: 2 }}>
        {activeTab === 0 && renderLessonList(pendingLessons, isPendingLoading, isPendingError)}
        {activeTab === 1 && renderLessonList(approvedLessons, isApprovedLoading, isApprovedError)}
      </Box>
    </Box>
  );
};
